import React, { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { formatDateISO, formatHumanDate, parseDateLoose } from "../../lib/formUtils";

const FLEX_OPTIONS = ["Exact", "± 1 day", "± 2 days", "± 3 days", "± 7 days"];

/**
 * Airbnb-style date range picker.
 * - Two months side-by-side (single month on mobile)
 * - Editorial styling (ink/gold on ivory)
 * - Flexibility chips at the bottom (Exact / ± 1..7 days)
 *
 * Props:
 *   value = { start, end } (ISO strings)
 *   onChange({ start, end })
 *   flexibility = string
 *   onFlexibilityChange(string)
 *   testId
 */
export default function DateRangeField({ value, onChange, flexibility, onFlexibilityChange, testId }) {
  const [open, setOpen] = useState(false);
  const start = value?.start ? parseDateLoose(value.start) : undefined;
  const end = value?.end ? parseDateLoose(value.end) : undefined;

  const display =
    start && end
      ? `${formatHumanDate(start)} → ${formatHumanDate(end)}`
      : start
      ? `${formatHumanDate(start)} → ...`
      : "Select a date range";

  const handleSelect = (range) => {
    onChange({ start: formatDateISO(range?.from), end: formatDateISO(range?.to) });
  };

  const setFlex = (label) => {
    if (onFlexibilityChange) onFlexibilityChange(label);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="field-editorial pr-10 text-left flex items-center justify-between gap-2"
          data-testid={testId}
        >
          <span className={start ? "text-ink" : "text-[#9A9A9A]"}>{display}</span>
          <CalendarIcon size={16} strokeWidth={1.25} className="text-muted" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 bg-ivory border border-hairline shadow-xl"
        align="start"
        sideOffset={8}
        data-testid={testId ? `${testId}-popover` : undefined}
      >
        {/* Status header — shows what's selected + guides the user */}
        <div className="px-5 pt-5 pb-3 border-b border-hairline">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-muted">Departure</div>
              <div className={`font-serif text-lg mt-1 ${start ? "text-ink" : "text-muted/50"}`}>
                {start ? formatHumanDate(start) : "Pick a start date"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-muted">Return</div>
              <div className={`font-serif text-lg mt-1 ${end ? "text-ink" : (start ? "text-gold" : "text-muted/50")}`}>
                {end ? formatHumanDate(end) : (start ? "Now pick your return date" : "—")}
              </div>
            </div>
          </div>
        </div>
        <div className="px-5 pt-4">
          <Calendar
            mode="range"
            selected={{ from: start, to: end }}
            onSelect={handleSelect}
            numberOfMonths={2}
            defaultMonth={start || new Date()}
            disabled={{ before: new Date() }}
            className="p-0"
            classNames={{
              months: "flex flex-col sm:flex-row gap-6 sm:gap-8",
              month: "space-y-3",
              caption: "flex justify-center pt-1 relative items-center h-9",
              caption_label: "font-serif text-base text-ink tracking-wide",
              nav: "flex items-center gap-1",
              nav_button: "h-8 w-8 inline-flex items-center justify-center hover:bg-[#FBF7EC] text-ink transition-colors rounded-full",
              nav_button_previous: "absolute left-0",
              nav_button_next: "absolute right-0",
              head_row: "flex",
              head_cell: "text-muted uppercase tracking-[0.16em] rounded-none w-10 font-light text-[10px]",
              row: "flex w-full mt-1",
              cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-transparent",
              day: "h-10 w-10 p-0 font-light text-ink hover:bg-[#FBF7EC] hover:text-gold rounded-full transition-colors aria-selected:opacity-100",
              day_range_start: "!bg-ink !text-ivory rounded-full font-normal",
              day_range_end: "!bg-ink !text-ivory rounded-full font-normal",
              day_selected: "!bg-ink !text-ivory hover:!bg-ink hover:!text-ivory focus:!bg-ink focus:!text-ivory",
              day_today: "border border-gold text-ink",
              day_outside: "text-muted/40",
              day_disabled: "text-muted/40 line-through cursor-not-allowed hover:bg-transparent",
              day_range_middle: "!bg-[#F3EDE1] !text-ink !rounded-none hover:!bg-[#EFE7D3]",
              day_hidden: "invisible",
            }}
          />
        </div>
        <div className="px-5 pt-4 pb-5 mt-2 border-t border-hairline flex flex-wrap gap-2 items-center" data-testid={testId ? `${testId}-flex-chips` : undefined}>
          <span className="text-[10px] uppercase tracking-[0.24em] text-muted mr-1">Flexibility</span>
          {FLEX_OPTIONS.map((opt) => {
            const active = flexibility === opt || (!flexibility && opt === "Exact");
            return (
              <button
                key={opt}
                type="button"
                onClick={() => setFlex(opt)}
                className={`px-3.5 h-8 text-[11px] uppercase tracking-[0.18em] transition-colors rounded-full border ${
                  active
                    ? "bg-ink text-ivory border-ink"
                    : "bg-transparent text-ink border-hairline hover:border-ink"
                }`}
                data-testid={testId ? `${testId}-flex-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}` : undefined}
              >
                {opt === "Exact" ? "Exact dates" : opt}
              </button>
            );
          })}
          <div className="ml-auto">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 h-8 text-[11px] uppercase tracking-[0.24em] bg-gold text-ink hover:bg-ink hover:text-ivory transition-colors"
              data-testid={testId ? `${testId}-done` : undefined}
            >
              Done
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
